import type { ComponentProps, ReactNode } from 'react';
import { useRef } from 'react';
import {
  DatePicker,
  DateRangePicker,
  Dialog,
  Popover,
  Group,
  FieldError,
  Label,
  Button as CalendarTrigger,
} from 'react-aria-components';
import { I18nProvider } from 'react-aria';
import { CalendarIcon } from 'lucide-react';
import { CloseCircle } from 'iconsax-react';

import type {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
} from '@internationalized/date';

import { DateInput } from '@src/design-system/datepicker';
import { Calendar, RangeCalendar } from '@src/design-system/calender';
import { cn } from '@src/design-system/helpers';

/* ---------- Updated: CalendarPopover supports single/range ---------- */

export const CalendarPopover = ({
  className,
  mode = 'single',
  ...props
}: ComponentProps<typeof Popover> & { mode?: 'single' | 'range' }) => {
  return (
    <Popover
      className={cn(
        'bg-white text-gray-900 data-entering:animate-in data-exiting:animate-out data-entering:fade-in-0 data-exiting:fade-out-0 data-entering:zoom-in-95 data-exiting:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=left]:slide-in-from-right-2 data-[placement=right]:slide-in-from-left-2 data-[placement=top]:slide-in-from-bottom-2 z-50 rounded-lg border border-pale-border shadow-lg outline-hidden pointer-events-auto',
        className
      )}
      {...props}
    >
      <Dialog className='max-h-[inherit] overflow-auto p-2'>
        {mode === 'range' ? <RangeCalendar /> : <Calendar />}
      </Dialog>
    </Popover>
  );
};

/* ----------------------- Single-date (existing) ---------------------- */

const dateValidationMapper = (
  date: CalendarDate | ZonedDateTime | CalendarDateTime
) => new Map([[String(date.year).length !== 4, 'Enter valid date']]);

export function DatePickerField({
  suffixIcon,
  containerClassName,
  triggerClassName,
  inputProps,
  label = 'Date',
  calenderTrigger,
  withErrorMessage = true,
  onRemove,
  ...props
}: ComponentProps<typeof DatePicker> & {
  suffixIcon?: ReactNode;
  containerClassName?: string;
  triggerClassName?: string;
  inputProps?: ComponentProps<typeof DateInput>;
  calenderTrigger?: ReactNode;
  onRemove?: () => void;
  label?: string;
  withErrorMessage?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <I18nProvider locale='en-GB'>
      <DatePicker
        validate={(date) => dateValidationMapper(date).get(true)}
        className={cn('group *:not-first:mt-2', containerClassName)}
        {...props}
      >
        {label && (
          <Label className='text-sm font-medium text-gray-800 leading-none'>
            {label}
          </Label>
        )}

        {calenderTrigger ? (
          <CalendarTrigger
            ref={ref}
            name='custom-trigger'
            className={cn('w-max', triggerClassName)}
          >
            {calenderTrigger}
          </CalendarTrigger>
        ) : (
          <div className='flex'>
            <Group className='relative w-full'>
              <DateInput
                className={cn('pe-9', inputProps?.className)}
                {...inputProps}
              />
              {props.value && onRemove && (
                <CloseCircle
                  size={16}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.();
                  }}
                  className='absolute opacity-60 transition-colors hover:opacity-100 group-has-disabled:hidden mr-8 right-0 top-1/2 -translate-y-1/2'
                />
              )}
            </Group>
            <CalendarTrigger
              ref={ref}
              className={cn(
                'text-gray-600 group-has-disabled:opacity-50 group-has-disabled:hover:text-gray-600 hover:text-gray-900 data-focus-visible:border-ring data-focus-visible:ring-ring/20 z-10 -ms-9 -me-px flex w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none data-focus-visible:ring-[3px]',
                triggerClassName
              )}
            >
              {suffixIcon ?? <CalendarIcon size={16} />}
            </CalendarTrigger>
          </div>
        )}

        <CalendarPopover
          triggerRef={ref}
          mode='single'
        />
        {withErrorMessage && (
          <FieldError className='text-xs text-destructive' />
        )}
      </DatePicker>
    </I18nProvider>
  );
}

/* ----------------------- New: Date RANGE picker ---------------------- */

type RangeDateValue =
  | {
      start?: CalendarDate | ZonedDateTime | CalendarDateTime;
      end?: CalendarDate | ZonedDateTime | CalendarDateTime;
    }
  | undefined;

export function DateRangePickerField({
  suffixIcon,
  containerClassName,
  triggerClassName,
  startInputProps,
  endInputProps,
  label = 'Date range',
  calenderTrigger,
  onRemove,
  ...props
}: ComponentProps<typeof DateRangePicker> & {
  suffixIcon?: ReactNode;
  containerClassName?: string;
  triggerClassName?: string;
  startInputProps?: ComponentProps<typeof DateInput>;
  endInputProps?: ComponentProps<typeof DateInput>;
  calenderTrigger?: ReactNode;
  onRemove?: () => void;
  label?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  // Simple validation: 4-digit years and start <= end (based on yyyymmdd)
  const fourDigitYear = (
    d?: CalendarDate | ZonedDateTime | CalendarDateTime
  ) => (d ? String(d.year).length === 4 : true);

  const dayNumber = (d?: CalendarDate | ZonedDateTime | CalendarDateTime) =>
    d ? d.year * 10000 + d.month * 100 + d.day : undefined;

  const validateRange = (range: RangeDateValue) => {
    const sOk = fourDigitYear(range?.start);
    const eOk = fourDigitYear(range?.end);
    if (!sOk || !eOk) return 'Enter valid date';
    const sNum = dayNumber(range?.start);
    const eNum = dayNumber(range?.end);
    if (sNum !== undefined && eNum !== undefined && sNum > eNum) {
      return 'End date must be after start date';
    }
    return undefined;
  };

  const hasValue = Boolean(
    (props.value as RangeDateValue)?.start ||
    (props.value as RangeDateValue)?.end
  );

  return (
    <I18nProvider locale='en-GB'>
      <DateRangePicker
        validate={validateRange}
        className={cn('group *:not-first:mt-2', containerClassName)}
        {...props}
      >
        {label && (
          <Label className='text-sm font-medium text-gray-800 leading-none'>
            {label}
          </Label>
        )}

        {calenderTrigger ? (
          <CalendarTrigger
            ref={ref}
            slot='trigger'
            name='custom-trigger'
            className={cn('w-max', triggerClassName)}
          >
            {calenderTrigger}
          </CalendarTrigger>
        ) : (
          <div className='flex'>
            <Group className='relative w-full'>
              {/* START input */}
              <DateInput
                slot='start'
                className={cn('pe-2', startInputProps?.className)}
                {...startInputProps}
              />
              <span
                aria-hidden
                className='px-1 text-muted-foreground'
              >
                —
              </span>
              {/* END input */}
              <DateInput
                slot='end'
                className={cn('pe-9', endInputProps?.className)}
                {...endInputProps}
              />

              {hasValue && onRemove && (
                <CloseCircle
                  size={16}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.();
                  }}
                  className='absolute opacity-60 transition-colors hover:opacity-100 group-has-disabled:hidden mr-8 right-0 top-1/2 -translate-y-1/2'
                />
              )}
            </Group>

            <CalendarTrigger
              ref={ref}
              slot='trigger'
              className={cn(
                'text-gray-600 group-has-disabled:opacity-50 group-has-disabled:hover:text-gray-600 hover:text-gray-900 data-focus-visible:border-ring data-focus-visible:ring-ring/20 z-10 -ms-9 -me-px flex w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none data-focus-visible:ring-[3px]',
                triggerClassName
              )}
            >
              {suffixIcon ?? <CalendarIcon size={16} />}
            </CalendarTrigger>
          </div>
        )}

        <CalendarPopover
          triggerRef={ref}
          mode='range'
        />
        <FieldError className='text-xs text-destructive' />
      </DateRangePicker>
    </I18nProvider>
  );
}
